/** @format */

import { Chain } from '@shared/data-access/interfaces';
import {
	ChangeDetectionStrategy,
	Component,
	ComponentRef,
	computed,
	effect,
	inject,
	input,
	signal,
	TemplateRef,
	viewChild
} from '@angular/core';
import { BreakpointsService, UtilityService } from '@shared/data-access/services';
import { HomrInStoreServices } from '@shared/homr/data-access';
import {
	HmfActionTextComponent,
	HmfButtonComponent,
	HmfDrawerComponent,
	HmfModalFactoryService
} from '@dsg-tech/homefield-angular';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import { CommonModule } from '@angular/common';
import { HomefieldIconLibraryModule } from '@dsg-tech/homefield-angular-icons';
import { HomefieldButtonModule } from '@homefield';
import { SideNavComponent } from '@shared/components';
import { MediaModule } from '@shared/media/components';

@Component({
	selector: 'pdp-in-store-services',
	templateUrl: './in-store-services.component.html',
	styleUrl: './in-store-services.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		HmfActionTextComponent,
		HmfButtonComponent,
		HomefieldButtonModule,
		HomefieldIconLibraryModule,
		MediaModule,
		SideNavComponent
	]
})
export class ProductInStoreServicesComponent {
	// Services
	private readonly chain = inject(Chain);
	readonly utilityService = inject(UtilityService);
	private readonly pdslUtilityService = inject(ProductSelectionUtilityService);
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly breakpointsService = inject(BreakpointsService);

	// Public readonly properties
	protected readonly chainIdentifierAbbr = this.chain.chainIdentifierAbbr;

	readonly homrInStoreServicesContent = input.required<HomrInStoreServices>();

	// Computed signal for decoded content
	protected readonly decodedContent = computed(() => {
		const content = this.homrInStoreServicesContent();

		return {
			...content,
			primaryPanelContent: content.primaryPanelContent
				? decodeURIComponent(content.primaryPanelContent)
				: content.primaryPanelContent,
			secondaryPanelContent: content.secondaryPanelContent
				? decodeURIComponent(content.secondaryPanelContent)
				: content.secondaryPanelContent
		};
	});

	private readonly drawerRef = signal<ComponentRef<HmfDrawerComponent> | null>(null);

	private readonly drawerContent = viewChild.required<TemplateRef<unknown>>('drawerContent');

	constructor() {
		// Effect for responsive drawer sizing
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			const drawer = this.drawerRef();

			if (drawer) {
				drawer.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
			}
		});
	}

	openDrawer(): void {
		let drawer = this.drawerRef();

		if (!drawer) {
			const content = this.decodedContent();

			drawer = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: content?.panelTitle,
					closable: true,
					mobilePeek: true,
					direction: 'right',
					isCloseButtonSticky: true,
					size: this.pdslUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.drawerContent()
			);

			this.drawerRef.set(drawer);
		}

		drawer.instance.openDrawer();
	}
}
